import { useLocalStorage } from '@vueuse/core'
import { computed } from 'vue'

import { useContactsStore } from '../stores/contacts'

/**
 * 线下模式状态管理（按联系人独立存储）
 * - 切换线下/线上模式
 * - 向聊天历史插入系统提示消息（仅 UI 展示，对 AI 不可见，由 UI_LAYER_SYSTEM_TYPES 过滤）
 */

// 按联系人存储线下模式状态
const offlineModeMap = useLocalStorage<Record<string, boolean>>('meowchat-offline-mode-map', {})

type OfflineSwitchMode = 'offline' | 'online'

/**
 * 切换系统消息的结构。push 进聊天历史，metadata.type==='offline_switch' 用于：
 * - UI 渲染（SystemMessage.vue）
 * - 过滤白名单（system-message-types.ts 的 UI_LAYER_SYSTEM_TYPES，不送进 LLM）
 */
interface OfflineSwitchMessage {
  role: 'system'
  content: string
  timestamp: number
  metadata: {
    type: 'offline_switch'
    mode: OfflineSwitchMode
    initiatedBy?: 'ai'
  }
}

/**
 * 消息列表的最小结构：只要能 push 一条切换消息即可。
 * content 设为可选，兼容 store 的 Array<ChatMessage | ErrorMessage>
 * （ChatAssistantMessage.content 可选）与后处理器上下文的宽松消息数组两种调用方。
 */
type SwitchableMessageList = Array<{ role: string, content?: unknown }>

export function useOfflineMode() {
  const contactsStore = useContactsStore()

  /**
   * 当前激活联系人的线下模式状态（响应式）
   * 切换联系人时自动切换到对应的线下模式状态
   */
  const isOfflineMode = computed({
    get: () => {
      const contactId = contactsStore.activeContactId
      if (!contactId)
        return false
      return offlineModeMap.value[contactId] || false
    },
    set: (val: boolean) => {
      const contactId = contactsStore.activeContactId
      if (!contactId)
        return
      const newMap = { ...offlineModeMap.value }
      if (val)
        newMap[contactId] = true
      else
        delete newMap[contactId]
      offlineModeMap.value = newMap
    },
  })

  /**
   * 向聊天历史插入一条线下/线上切换的系统消息（toggle 与 AI 主动切换共用）
   */
  function pushSwitchMessage(
    messages: SwitchableMessageList,
    mode: OfflineSwitchMode,
    content: string,
    onChanged?: () => void,
    initiatedBy?: 'ai',
  ) {
    const message: OfflineSwitchMessage = {
      role: 'system',
      content,
      timestamp: Date.now(),
      metadata: { type: 'offline_switch', mode, ...(initiatedBy ? { initiatedBy } : {}) },
    }
    messages.push(message)
    onChanged?.()
    console.info(`[offline-switch] ${content}`)
  }

  /**
   * 用户手动切换线下模式，并向 messages 数组中插入系统提示消息
   */
  function toggleOfflineMode(messages: SwitchableMessageList, onChanged?: () => void) {
    isOfflineMode.value = !isOfflineMode.value
    const mode: OfflineSwitchMode = isOfflineMode.value ? 'offline' : 'online'
    const content = isOfflineMode.value ? '已切换到线下模式' : '已切换到线上模式'
    pushSwitchMessage(messages, mode, content, onChanged)
  }

  /**
   * AI 主动切换线下模式（由后处理器调用）
   */
  function setOfflineModeByAI(
    mode: OfflineSwitchMode,
    characterName: string,
    messages: SwitchableMessageList,
    onChanged?: () => void,
    targetContactId?: string | null,
  ) {
    const targetValue = mode === 'offline'
    const contactId = targetContactId === undefined ? contactsStore.activeContactId : targetContactId
    const currentValue = contactId ? offlineModeMap.value[contactId] || false : isOfflineMode.value
    if (currentValue === targetValue)
      return

    if (contactId) {
      const nextMap = { ...offlineModeMap.value }
      if (targetValue)
        nextMap[contactId] = true
      else
        delete nextMap[contactId]
      offlineModeMap.value = nextMap
    }
    else {
      isOfflineMode.value = targetValue
    }
    const content = mode === 'offline'
      ? `${characterName}来到了你身边`
      : `你和${characterName}回到了线上聊天`
    pushSwitchMessage(messages, mode, content, onChanged, 'ai')
  }

  return {
    isOfflineMode,
    toggleOfflineMode,
    setOfflineModeByAI,
  }
}
